// MACHINERY V2 (Stroje) — Diely a materiál: checkbox, thumbnail, quantity pair
// (M2.5).
//
// Spec: data package §5. Reference impl:
// design_handoff_machinery_v3/machinery-profile-parts.jsx (`PcCheck`,
// `PcThumb`); artboards 1009 / 1011 / 1013.
//
// THE " / " SEPARATOR ALWAYS RENDERS — EVEN AT ZERO. §5, twice: "Separator ' / '
// ALWAYS renders (limit object always truthy)" and, in the designer footer,
// "„ / " oddeľovač sa vykresľuje vždy (aj pri 0)". v1 gets this incidentally,
// because `LimitedQuantityFormat` tests `!!limit` and `inStockQuantity` is a
// non-optional object; here it is unconditional, so a future nullable
// `inStockQuantity` cannot silently delete the separator. The stock side turns
// amber at zero (artboard 1009, the HGJ-250-3 row).
//
// UNITS COME FROM THE SHARED MAP. `unitCode()` (common/models/CommonModels.ts —
// a file with NO imports at all, so it is safe for the v2 import graph) turns
// the enum into the `common:measurementUnits.*` key, exactly as
// QuantityFormat.tsx does.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { unitCode } from "../../../../../common/models/CommonModels";
// TYPE-ONLY on purpose: shared/models/models.ts imports @mui/material on its
// first line, so an elided type import is the only safe reference (§3).
import type { Quantity } from "../../../../../shared/models/models";

/**
 * The parts select control.
 *
 * >>> THE PAINTED BOX AND THE HIT TARGET ARE NOT THE SAME ELEMENT BELOW 1280,
 * AND THAT IS THE WHOLE POINT (M2.16d). <<< The artboards draw a 16px box, and
 * 16px is legal for a pointer but a third of the ">= 44px both axes" floor this
 * module applies below 1280 — a floor this control cannot meet by growing,
 * because a 44px teal square is not a checkbox any more. So `touch` wraps the
 * SAME 16px paint in a transparent 44x44 button, exactly as
 * mobile/TouchChip.tsx wraps the mobile package's 34px pills. The row looks like
 * the artboard and touches like the standard.
 *
 * It is a PROP, not a media query in this file: this renders once per part row,
 * and useV2MediaQuery opens one matchMedia listener per instance (that hook's
 * header forbids per-row calls by name). PartsTabV2 reads the query once and
 * passes the boolean down.
 *
 * `touch` OMITTED IS TODAY'S DESKTOP ELEMENT, BYTE FOR BYTE — one button, the
 * same style object, no wrapper. Nothing the >= 1280 element never had is
 * emitted.
 *
 * The CSS floor in machinery-v2-touch.scss deliberately does NOT select this
 * control: it matches `aria-expanded` / `.btn`, and a checkbox carries
 * `aria-checked`. Growing it from a stylesheet would grow the PAINT — which is
 * precisely why `aria-pressed` was REMOVED from that selector at the M2.16d
 * review (F6): it was doing to the SR status chips and the metric/imperial
 * segment exactly what this component exists to avoid doing to a checkbox.
 * Those two now carry the same transparent-wrapper treatment as this one.
 */
export const PartCheckbox: React.FC<{
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onChange: () => void;
  touch?: boolean;
}> = ({ checked, indeterminate, label, onChange, touch }) => {
  const box: React.CSSProperties = {
    width: 16,
    height: 16,
    borderRadius: 4,
    flexShrink: 0,
    boxSizing: "border-box",
    border: "1.5px solid " + (checked || indeterminate ? "var(--mod-accent)" : "var(--border)"),
    background: checked || indeterminate ? "var(--mod-accent)" : "var(--card)",
    display: "grid",
    placeItems: "center",
  };

  const mark = (
    <React.Fragment>
      {checked && !indeterminate && (
        <svg viewBox="0 0 10 10" width="9" height="9" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
          <path d="M2 5.2 L4.2 7.4 L8 3" />
        </svg>
      )}
      {indeterminate && <span style={{ width: 7, height: 1.6, background: "#fff", borderRadius: 1 }} />}
    </React.Fragment>
  );

  // The row is itself clickable in places, so the stop is on BOTH paths: a
  // pointer activation and a keyboard one. `stopPropagation` only — calling
  // preventDefault on a button keydown would suppress the click the browser
  // synthesises from it, i.e. this control would stop working with the keyboard.
  const guard = {
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") e.stopPropagation();
    },
  };

  if (!touch) {
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={indeterminate ? "mixed" : checked}
        aria-label={label}
        {...guard}
        style={{ ...box, cursor: "pointer", padding: 0 }}
      >
        {mark}
      </button>
    );
  }

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? "mixed" : checked}
      aria-label={label}
      {...guard}
      style={{
        // 44x44 minimum, transparent — the span below is what the user sees.
        // No `minWidth: 0` follows this pair; one would silently void it.
        height: 44,
        minWidth: 44,
        display: "inline-grid",
        placeItems: "center",
        flexShrink: 0,
        border: 0,
        background: "transparent",
        padding: 0,
        cursor: "pointer",
      }}
    >
      <span style={box}>{mark}</span>
    </button>
  );
};

export const PartThumb: React.FC<{ src?: string | null; size?: number }> = ({ src, size = 42 }) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <span
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 7,
        overflow: "hidden",
        background: "var(--bg-soft)",
        border: "1px solid var(--border-soft)",
        display: "grid",
        placeItems: "center",
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 7.5,
            letterSpacing: "0.06em",
            color: "var(--faint)",
            textTransform: "uppercase",
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          {translate("myMachinery:v2.tabs.parts.noPhoto")}
        </span>
      )}
    </span>
  );
};

const QtyValue: React.FC<{ quantity?: Quantity | null; tone: string }> = ({ quantity, tone }) => {
  const { translate } = useTranslation("common");
  const unit = unitCode(quantity?.unitCode);
  return (
    <span style={{ color: tone }}>
      {quantity?.value}
      {unit ? " " + translate(`common:measurementUnits.${unit}`) : ""}
    </span>
  );
};

/** "installed / in stock" — see the header for why the separator is absolute. */
export const InstalledOverStock: React.FC<{
  installed?: Quantity | null;
  inStock?: Quantity | null;
}> = ({ installed, inStock }) => (
  <span
    style={{
      fontFamily: "var(--font-mono)",
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    }}
  >
    <b style={{ color: "var(--ink)", fontWeight: 700 }}>
      <QtyValue quantity={installed} tone="var(--ink)" />
    </b>
    <span style={{ color: "var(--faint)" }}> / </span>
    <QtyValue
      quantity={inStock}
      tone={
        inStock?.value === 0
          ? "color-mix(in oklab, var(--amber) 80%, var(--ink))"
          : "var(--muted)"
      }
    />
  </span>
);
